import { differenceInHours } from 'date-fns/differenceInHours'
import isEmpty from 'lodash/isEmpty'

import { localStorage } from 'bl-utils/src/storage/localStorage'

import { availableCurrencies } from '../../constants/availableCurrencies'
import { countryCodeCurrencyMap } from '../../constants/countryCodeCurrencyMap'

export type Rates = { EUR?: number; GBP?: number; ISK?: number; USD?: number }

type ExchangeRates = {
  rates: Rates
  mcpRates: Rates
  timeCreated?: string
  retrievalReferenceNumber?: string
}

type ExchangeRatesResponse = {
  mcp: {
    retrievalReferenceNumber: string
    rates: Rates
  }
  rates: Rates
}

type Currency = {
  ISO?: string
  countryCode?: string
  timeCreated?: string
}

export const currencyKey = 'BLUELAGOON_CURRENCY'
const rateKey = 'BLUELAGOON_EXCHANGE_RATE'
const defaultCurrency = 'ISK'
const DEFAULT_COUNTRY_CODE = 'IS'

/*
  This function checks if the exchange rates are still valid.
  It is used to bypass the localStorage cache and fetch the latest rates from the API.
  The exchange rates are valid for 4 hours.
*/
const isValid = (timestamp: string) => {
  const timeLimitHours = 4
  const now = new Date()
  const timeCreated = new Date(timestamp)

  return differenceInHours(now, timeCreated) < timeLimitHours
}

const getExchangeRates = async () => {
  const exchangeRates = JSON.parse(
    localStorage.getItem(rateKey)
  ) as ExchangeRates // check localstorage

  if (exchangeRates && isValid(exchangeRates.timeCreated)) {
    return exchangeRates
  }

  try {
    const response = await fetch('/api/exchange-rates')
    if (!response.ok) {
      throw new Error(`Failed to fetch exchange rates: ${response.statusText}`)
    }
    const res = (await response.json()) as ExchangeRatesResponse

    const retrievalReferenceNumber = res?.mcp.retrievalReferenceNumber || null
    const rates = res?.rates
    const mcpRates = res?.mcp.rates

    if (rates && !isEmpty(rates) && retrievalReferenceNumber) {
      const exchangeRatesToLocalStorage = {
        rates,
        mcpRates,
        timeCreated: new Date().toISOString(),
        retrievalReferenceNumber,
      }
      localStorage.setItem(rateKey, JSON.stringify(exchangeRatesToLocalStorage))

      return exchangeRatesToLocalStorage
    }

    return null
  } catch {
    return null
  }
}

const getCurrency = (country: string) => {
  const res = localStorage.getItem(currencyKey) // check localstorage
  const currency = res ? (JSON.parse(res) as Currency) : null

  // Return the localstorage currency if it is valid.
  if (currency?.countryCode && currency.ISO) {
    return { currency: currency.ISO, countryCode: currency.countryCode }
  }

  const countryCodeToUse = country || DEFAULT_COUNTRY_CODE
  const countryCurrency = countryCodeCurrencyMap[countryCodeToUse]
  const currencyToUse =
    availableCurrencies.find(currency => currency.ISO === countryCurrency)
      .ISO || defaultCurrency

  // save new currency to localstorage
  localStorage.setItem(
    currencyKey,
    JSON.stringify({
      countryCode: countryCodeToUse,
      ISO: currencyToUse,
      timeCreated: new Date().toISOString(),
    })
  )

  return { currency: currencyToUse, countryCode: countryCodeToUse }
}

export const getCurrencyInformation = async (country: string) => {
  return await Promise.all([getCurrency(country), getExchangeRates()])
}
